import type { FC, ReactNode } from 'react'

import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaBand } from '@/components/generals/cta-band'
import { Landscape } from '@/components/generals/landscape'
import { PageHero } from '@/components/generals/page-hero'
import { Reveal } from '@/components/generals/reveal'
import { SplitHeading } from '@/components/generals/split-heading'
import { TextLink } from '@/components/generals/text-link'
import { LANDSCAPES } from '@/lib/visual'

import {
    BudgetPreview,
    InvestPreview,
    PlanPreview,
    ProtectPreview
} from '@/components/landing/services/service-previews'

interface Service {
    id: string
    number: string
    name: string
    title: string
    body: string
    points: Array<string>
    image: string
    position: string
    link: { label: string, href: string }
    preview: ReactNode
}

const SERVICES: Array<Service> = [
    {
        id: 'budgeting',
        number: '01',
        name: 'Budgeting',
        title: 'Every dollar, *quietly placed*',
        body: 'Each purchase is filed the moment it clears, limits shape themselves around the way you really live, and every morning brings one clear number you can spend.',
        points: ['Categories that learn from your corrections', 'One spendable number, fresh each morning', 'Upcoming bills spotted days ahead'],
        image: LANDSCAPES.wildflowers,
        position: 'center 60%',
        link: { label: 'Open the savings goal calculator', href: '/tools#savings' },
        preview: <BudgetPreview />
    },
    {
        id: 'investing',
        number: '02',
        name: 'Investing',
        title: 'Patient money, *steadily grown*',
        body: 'Diversified, low-fee portfolios shaped by what you are saving for and how much swing you can sit with, kept in balance and explained without jargon.',
        points: ['Built from broad index funds and ETFs', 'Rebalancing and tax-loss harvesting, handled for you', 'Values-led portfolios if they matter to you'],
        image: LANDSCAPES.goldenTree,
        position: 'center 55%',
        link: { label: 'Talk with an investing advisor', href: '/advisors?specialty=Investing' },
        preview: <InvestPreview />
    },
    {
        id: 'planning',
        number: '03',
        name: 'Planning',
        title: 'Your years, *mapped with care*',
        body: 'A year off, a first home, children, a slower pace later. Verdane places each goal on one timeline and shows what every choice costs before you commit.',
        points: ['Goals ranked by what matters to you', 'Any what-if, answered in seconds', 'A real person to talk it through, any time'],
        image: LANDSCAPES.windingPath,
        position: 'center 55%',
        link: { label: 'Try the retirement calculator', href: '/tools#retirement' },
        preview: <PlanPreview />
    },
    {
        id: 'protection',
        number: '04',
        name: 'Protection',
        title: 'Steady cover, *day and night*',
        body: 'Rainy-day savings, odd charges, gaps in your cover. Verdane keeps an eye on the edges of your money so everything in between can rest.',
        points: ['Guidance on the right emergency cushion', 'Instant alerts for unusual or suspicious charges', 'Cover gaps raised before they cost you'],
        image: LANDSCAPES.cypressVilla,
        position: 'center 50%',
        link: { label: 'See how we protect your data', href: '/ai-intelligence#privacy' },
        preview: <ProtectPreview />
    }
]

const ServiceStory = ({ service, index }: { service: Service, index: number }) => {
    const reversed = index % 2 === 1

    return (
        <section id={service.id} aria-labelledby={`${service.id}-title`} className='relative scroll-mt-24 py-20 sm:py-28'>
            <div className='shell grid items-center gap-12 lg:grid-cols-2 lg:gap-20'>
                <div className={cn(reversed ? 'lg:order-2' : 'lg:order-1')}>
                    <Reveal y={10} className='flex items-center gap-4'>
                        <span className='font-serif text-5xl italic leading-none text-citron'>
                            {service.number}
                        </span>

                        <span className='h-px w-12 bg-white/25' />

                        <span className='text-[11px] font-medium uppercase tracking-[0.34em] text-cream/60'>
                            {service.name}
                        </span>
                    </Reveal>

                    <SplitHeading
                        as='h2'
                        id={`${service.id}-title`}
                        text={service.title}
                        className='mt-7 text-balance text-[2.5rem] font-normal leading-[1] tracking-[-0.045em] text-cream sm:text-6xl'
                    />

                    <Reveal as='p' delay={250} className='mt-6 max-w-md text-[16px] leading-relaxed text-cream/70'>
                        {service.body}
                    </Reveal>

                    <ul className='mt-8 space-y-3'>
                        {service.points.map((point, pointIndex) => (
                            <Reveal as='li' key={point} delay={350 + pointIndex * 110} y={12} className='flex items-center gap-3 text-[15px] text-cream/85'>
                                <span className='grid size-6 shrink-0 place-items-center rounded-full bg-citron/15 text-citron'>
                                    <Check aria-hidden='true' className='size-3.5' />
                                </span>
                                {point}
                            </Reveal>
                        ))}
                    </ul>

                    <Reveal delay={700} className='mt-9'>
                        <TextLink href={service.link.href}>
                            {service.link.label}
                        </TextLink>
                    </Reveal>
                </div>

                <Reveal x={reversed ? -60 : 60} y={20} blur={6} className={cn('w-full', reversed ? 'lg:order-1' : 'lg:order-2')}>
                    <div className='grain group relative isolate flex min-h-[520px] items-end justify-center overflow-hidden rounded-[36px] bg-ink p-4 sm:min-h-[600px] sm:p-8'>
                        <Landscape
                            src={service.image}
                            fade='card'
                            position={service.position}
                            sizes='(min-width: 1024px) 50vw, 100vw'
                            imageClassName='group-hover:scale-[1.04]'
                            className='-z-10'
                        />
                        <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />

                        <div className='w-full max-w-[440px]'>
                            {service.preview}
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}

const WhatWeDo: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='What we do'
                description='Budgeting, investing, planning and protection, held together in one place. See how Verdane and your AI advisor care for every part of your money.'
            />

            <PageHero
                eyebrow='Our services'
                title={'Four ways we help,\n*working as one*'}
                subtitle='Budgeting, investing, planning and protection, joined up by an AI advisor that sees all of your money, and backed by real people when you want a voice.'
                image={LANDSCAPES.mistyRidges}
                position='center 70%'
            >
                <Reveal as='nav' trigger='mount' delay={900} className='mt-10 flex flex-wrap items-center justify-center gap-2' aria-label='Services'>
                    {SERVICES.map(service => (
                        <a key={service.id} href={`#${service.id}`} className='glass rounded-full px-4 py-2 text-sm text-cream/80 transition-colors hover:text-cream'>
                            <span className='mr-2 font-serif italic text-citron'>
                                {service.number}
                            </span>
                            {service.name}
                        </a>
                    ))}
                </Reveal>
            </PageHero>

            {SERVICES.map((service, index) => <ServiceStory key={service.id} service={service} index={index} />)}

            <CtaBand
                eyebrow='Begin here'
                title={'Every part of your money,\n*in one place*'}
                image='/images/uploads/background.png'
                position='center 62%'
            />
        </Layout>
    )
}

export default WhatWeDo
